import {
  AgentsCard,
  Connector,
  ControlPlane,
  DECISION_CARD_CLASS,
  DecisionApi,
  EDGE,
  EndpointCard,
  ENDPOINTS,
  LogoNode,
} from './atoms';
import { at, drawLine, growFrom, pulse } from './timelineHelpers';
import { useRailsTimeline, type BuildRailsTimeline } from './useRailsTimeline';

const ID = {
  agents: 'rails-agents',
  connectA: 'rails-connect-a',
  ring: 'rails-logo-ring',
  plane: 'rails-plane',
  connectDecision: 'rails-connect-decision',
  decision: 'rails-decision',
  connectEndpoint: 'rails-connect-endpoint',
  endpoint: 'rails-endpoint',
};

const buildDesktopTimeline: BuildRailsTimeline = ({ tl, q, gate }) => {
  // 1. Agents card: outline draws from the left edge and closes on the right
  //    edge, where the connector leaves.
  tl.addLabel('agents', 0.5);
  growFrom(tl, ID.agents, 'agents', {
    entry: EDGE.left,
    to: 50,
    duration: 0.6,
  });

  // 2. Connector to the control plane, lighting the Transcend logo ring on
  //    the way (the logo covers ~28%–72% of the line).
  tl.addLabel('connectA', at('agents', 0.6)).fromTo(
    `#${ID.connectA}`,
    { drawSVG: '0% 0%' },
    { drawSVG: '0% 28%', duration: 0.2, ease: 'none' },
    'connectA',
  );
  // Ring: arrives at 9 o'clock (50% round the circle), closes at 3 o'clock
  growFrom(tl, ID.ring, at('connectA', 0.2), {
    entry: 50,
    to: 50,
    duration: 0.35,
  });
  tl.to(
    `#${ID.connectA}`,
    { drawSVG: '0% 100%', duration: 0.2, ease: 'none' },
    at('connectA', 0.55),
  );

  // 3. The line reaches the control plane. Its outline draws out from the
  //    left edge, both halves stopping at the top and bottom midpoints (half
  //    the outline lit) — the bottom one right where the decision line leaves.
  tl.addLabel('plane', at('connectA', 0.75));
  growFrom(tl, ID.plane, 'plane', {
    entry: EDGE.left,
    to: 25,
    duration: 0.4,
  });

  // 4. Decision line draws down and the Decision API lights up fully.
  drawLine(tl, ID.connectDecision, at('plane', 0.4), 0.15);
  growFrom(tl, ID.decision, at('plane', 0.55), {
    entry: EDGE.top,
    to: 50,
    duration: 0.35,
  });

  // 5. Making the decision: the card pulses while lit, then the gate decides.
  //    "No" fades everything out and restarts here.
  tl.addLabel('decide', at('plane', 0.95));
  const pulsed = pulse(tl, q(`.${DECISION_CARD_CLASS}`), 'decide');
  tl.call(gate, undefined, at('decide', pulsed + 0.1));

  // 6. "Yes": finish drawing the outline round to the right edge, then fan
  //    out to the endpoints from there.
  growFrom(tl, ID.plane, at('decide', pulsed + 0.12), {
    entry: EDGE.left,
    from: 25,
    to: 50,
    duration: 0.4,
  });
  tl.addLabel('endpoints', at('decide', pulsed + 0.5));
  ENDPOINTS.forEach((_, index) => {
    const stagger = index * 0.08;
    drawLine(
      tl,
      `${ID.connectEndpoint}-${index}`,
      at('endpoints', stagger),
      0.4,
    );
    growFrom(tl, `${ID.endpoint}-${index}`, at('endpoints', 0.4 + stagger), {
      entry: EDGE.left,
      to: 50,
      duration: 0.45,
    });
  });
};

export function DesktopAnimation() {
  const { container, activeButton } = useRailsTimeline(buildDesktopTimeline);

  return (
    // pb leaves room for the Decision API hanging below the control plane
    // (40px connector + 75px card, minus the half-height difference between
    // the plane and the taller agents card).
    // The two side groups are equal-width flex columns so the control plane
    // sits dead centre; the connectors stretch to fill whatever's left, which
    // makes the left one longer (agents card is narrower than the endpoints).
    // max-w matches the Figma frame's diagram width so the lines don't run on
    // at wide viewports.
    <div
      ref={container}
      className="mx-auto flex w-full max-w-[1203px] items-center pb-100"
    >
      <div className="flex min-w-0 flex-1 items-center">
        <AgentsCard borderId={ID.agents} activeButton={activeButton} />
        <div className="relative flex min-w-0 flex-1 items-center justify-center">
          <Connector
            id={ID.connectA}
            orientation="horizontal"
            className="absolute inset-x-0 top-1/2 w-full -translate-y-1/2"
          />
          <LogoNode ringId={ID.ring} />
        </div>
      </div>

      <div className="relative shrink-0">
        <ControlPlane borderId={ID.plane} />
        <div className="absolute left-1/2 top-full flex -translate-x-1/2 flex-col items-center">
          <Connector
            id={ID.connectDecision}
            orientation="vertical"
            className="h-[40px]"
          />
          <DecisionApi borderId={ID.decision} />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-12">
        {ENDPOINTS.map((endpoint, index) => (
          <div key={endpoint.title} className="flex items-center">
            <Connector
              id={`${ID.connectEndpoint}-${index}`}
              orientation="horizontal"
              className="min-w-0 flex-1"
            />
            <EndpointCard borderId={`${ID.endpoint}-${index}`} {...endpoint} />
          </div>
        ))}
      </div>
    </div>
  );
}
